import { FormProvider } from "react-hook-form";
import { classNames } from "primereact/utils";
import { AppDialog } from "../../../../common/components/AppDialog/AppDialog";
import { DefaultButton } from "../../../../common/components/button/defaultButton/DefaultButton";
import { IconButton } from "../../../../common/components/button/iconButton/IconButton";
import { DefaultInputField } from "../../../../common/components/forms/inputs/InputField/DefaultInputField";
import { QrCodePreview } from "../../../../common/components/qrCode/QrCodePreview";
import { SkeletonItem } from "../../../../common/components/skeletons/SkeletonItem";
import { IconType } from "../../../../common/icons/Icon";
import { GiftVoucherStatus } from "../../../../common/enums";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import { GiftVoucherTransactionTypeLabels } from "../../../../common/labels";
import { useGiftVoucherDetailsDialog } from "../hooks/useGiftVoucherDetailsDialog";
import {
  formatVoucherAmount,
  formatVoucherCode,
  formatVoucherHolder,
  getVoucherDisplayAmounts,
  getVoucherStatusKey,
  getVoucherStatusLabel,
} from "../utils/giftVoucherUtils";
import styles from "./GiftVoucherDetailsDialog.module.scss";

interface IGiftVoucherDetailsDialogProps {
  voucherId: number | null;
  visible: boolean;
  onHide: () => void;
}

export const GiftVoucherDetailsDialog = ({
  voucherId,
  visible,
  onHide,
}: IGiftVoucherDetailsDialogProps) => {
  const {
    voucher,
    transactions,
    isLoading,
    copyCode,
    emailForm,
    isSendingEmail,
    sendEmail,
  } = useGiftVoucherDetailsDialog({ voucherId });

  return (
    <AppDialog
      header={voucher?.name ?? "Ваучер"}
      visible={visible}
      className={styles.dialog}
      onHide={onHide}
    >
      <RenderWhen condition={isLoading || !voucher}>
        <SkeletonItem isLoading value={null} width="100%" />
      </RenderWhen>

      <RenderWhen condition={!isLoading && !!voucher}>
        <div className={styles.root}>
          <div className={styles.root_summary}>
            <span
              className={classNames(
                styles.root_summary_badge,
                voucher &&
                  styles[
                    `root_summary_badge_${getVoucherStatusKey(voucher)}`
                  ],
              )}
            >
              {voucher && getVoucherStatusLabel(voucher)}
            </span>
            <span className={styles.root_summary_remaining}>
              {voucher && getVoucherDisplayAmounts(voucher).primary}
            </span>
            <span className={styles.root_summary_initial}>
              {voucher && getVoucherDisplayAmounts(voucher).secondary}
            </span>
          </div>

          {/* The code is what lets anyone spend the balance, so it lives here (a
              manager-only screen) and never in the cashdesk's sellable grid. */}
          <div className={styles.root_code}>
            <span className={styles.root_code_value}>
              {formatVoucherCode(voucher?.code ?? "")}
            </span>
            <IconButton
              iconType={IconType.Copy}
              iconHeight="20px"
              iconWidth="20px"
              onClick={copyCode}
              tooltip="Копирай кода"
              ariaLabel="Копирай кода"
            />
          </div>

          <RenderWhen condition={!!voucher?.code}>
            <QrCodePreview
              value={voucher?.code ?? ""}
              name={`voucher-${voucher?.code ?? ""}`}
            />
          </RenderWhen>

          <dl className={styles.root_facts}>
            <dt>Валиден до</dt>
            <dd>
              {voucher?.validUntil
                ? dateUtility.formatDate(voucher.validUntil)
                : "Безсрочен"}
            </dd>

            <dt>Продаден</dt>
            <dd>
              {voucher?.soldOn
                ? `${dateUtility.formatDateTime(voucher.soldOn)} (поръчка #${String(
                    voucher.soldInOrderNumber,
                  ).padStart(4, "0")})`
                : "Още не е продаден"}
            </dd>

            <dt>Държи се от</dt>
            <dd>{voucher ? formatVoucherHolder(voucher) : ""}</dd>

            <dt>Създаден от</dt>
            <dd>
              {voucher?.createdByName} на{" "}
              {dateUtility.formatDate(voucher?.createdOn)}
            </dd>
          </dl>

          {/*
            Delivery, not a state change: sending the voucher moves no money and does
            not mark it sold, so it can be re-sent to a different address freely.
          */}
          <FormProvider {...emailForm}>
            <form className={styles.root_email} onSubmit={sendEmail}>
              <h4 className={styles.root_email_title}>Изпрати по имейл</h4>

              <div className={styles.root_email_row}>
                <DefaultInputField
                  name="email"
                  placeholder="получател@example.com"
                  width="100%"
                />
                <DefaultButton
                  label="Изпрати"
                  type="submit"
                  design="outline"
                  width="auto"
                  loading={isSendingEmail}
                />
              </div>

              {/* Emailing is delivery only — it does not sell the voucher. A code sent
                  before the sale is rung up will be refused at the counter (32008). */}
              <RenderWhen condition={voucher?.status === GiftVoucherStatus.New}>
                <span className={styles.root_email_warning}>
                  Ваучерът още не е продаден. Кодът няма да работи, докато не бъде продаден
                  през касата.
                </span>
              </RenderWhen>

              <RenderWhen condition={!!voucher?.lastEmailSentOn}>
                <span className={styles.root_email_last}>
                  Последно изпратен на {voucher?.lastEmailSentTo} (
                  {dateUtility.formatDateTime(voucher?.lastEmailSentOn)})
                </span>
              </RenderWhen>
            </form>
          </FormProvider>

          {/* The money trail: every row says what a piece of the voucher became. */}
          <div className={styles.root_ledger}>
            <h4 className={styles.root_ledger_title}>Движения</h4>

            <RenderWhen condition={transactions.length === 0}>
              <p className={styles.root_ledger_empty}>
                Няма движения. Ваучерът още не е продаден.
              </p>
            </RenderWhen>

            {transactions.map((transaction) => (
              <div className={styles.root_ledger_row} key={transaction.id}>
                <div className={styles.root_ledger_row_main}>
                  <span className={styles.root_ledger_row_main_type}>
                    {GiftVoucherTransactionTypeLabels[transaction.type]}
                  </span>
                  <span className={styles.root_ledger_row_main_comment}>
                    {transaction.comment}
                  </span>
                </div>

                <div className={styles.root_ledger_row_side}>
                  <span
                    className={classNames(styles.root_ledger_row_side_amount, {
                      [styles.root_ledger_row_side_amount_negative]:
                        transaction.amount < 0,
                    })}
                  >
                    {transaction.amount > 0 ? "+" : ""}
                    {formatVoucherAmount(transaction.amount)}
                  </span>
                  <span className={styles.root_ledger_row_side_meta}>
                    {dateUtility.formatDateTime(transaction.createdOn)}
                    {transaction.createdByName
                      ? ` · ${transaction.createdByName}`
                      : ""}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </RenderWhen>
    </AppDialog>
  );
};
